import { bpsOf, roundHalfUp } from './round';
import type {
  ChannelInput,
  FeeBreakdown,
  FixedFeeRange,
  PriceEvaluation,
  PriceState,
} from './types';

export function fixedFeeRangeAt(price: number, channel: ChannelInput): FixedFeeRange | null {
  return (
    channel.fixedFees.find(
      (r) => price >= r.fromCentavos && (r.toCentavos === null || price <= r.toCentavos),
    ) ?? null
  );
}

export function sellerPaysShippingAt(price: number, channel: ChannelInput): boolean {
  const s = channel.shipping;
  if (!s || s.averageCentavos <= 0) return false;
  return s.fromCentavos === null || price >= s.fromCentavos;
}

/** The fixed (price-independent) part of the fees at a price: fixed fee + shipping share. */
export function fixedPartAt(price: number, channel: ChannelInput): number {
  const range = fixedFeeRangeAt(price, channel);
  return (
    (range?.feeCentavos ?? 0) +
    (sellerPaysShippingAt(price, channel) ? (channel.shipping?.averageCentavos ?? 0) : 0)
  );
}

/** Every fee the channel takes at a given price, each rounded to the centavo. */
export function feesAt(price: number, channel: ChannelInput, taxBps: number): FeeBreakdown {
  const range = fixedFeeRangeAt(price, channel);
  const sellerPaysShipping = sellerPaysShippingAt(price, channel);
  const commission = roundHalfUp(bpsOf(price, channel.commissionBps));
  const fixedFee = range?.feeCentavos ?? 0;
  const shipping = sellerPaysShipping ? (channel.shipping?.averageCentavos ?? 0) : 0;
  const paymentFee = roundHalfUp(bpsOf(price, channel.paymentFeeBps));
  const tax = roundHalfUp(bpsOf(price, taxBps));
  return {
    commission,
    fixedFee,
    fixedFeeRange: range,
    shipping,
    sellerPaysShipping,
    paymentFee,
    tax,
    total: commission + fixedFee + shipping + paymentFee + tax,
  };
}

/** net profit = price − unit cost − commission − fixed fee − shipping share − payment fee − tax */
export function evaluate(
  price: number,
  source: PriceEvaluation['source'],
  unitCost: number,
  channel: ChannelInput,
  opts: { taxBps: number; minimumMarginBps: number },
): PriceEvaluation {
  const fees = feesAt(price, channel, opts.taxBps);
  const netProfitCentavos = price - unitCost - fees.total;
  const marginBps = price > 0 ? roundHalfUp((netProfitCentavos / price) * 10_000) : 0;
  let state: PriceState = 'ok';
  if (netProfitCentavos < 0) state = 'below_cost';
  else if (marginBps < opts.minimumMarginBps) state = 'below_minimum_margin';
  return { priceCentavos: price, source, fees, netProfitCentavos, marginBps, state };
}
